import { useGlobalConfigContext } from 'GlobalConfig.context';
import Shipment from 'interfaces/PmParcel';
import { Campaign } from 'models/Campaign.model';
import { useEffect, useRef, useState } from 'preact/hooks';
import { agentService } from 'services/agent.services';
import {
  CampaignsDTOResponse,
  getCampaignLiveMode,
  getCampaignsPreviewMode,
} from 'services/campaign.services';
import { ptpCatchError } from 'services/sentry.services';
import { isRuntimeError } from 'utils/fetcher.utils';
import { getParameterByName } from 'utils/queryString.utils';

interface CampaignsHookProps {
  shipment: Shipment;
}

export const useCampaigns = ({ shipment }: CampaignsHookProps) => {
  const campaignUuid = getParameterByName('preview_campaign_uuid');
  const { organizationId, cmsSlug } = useGlobalConfigContext();

  const [data, setData] = useState<CampaignsDTOResponse>(
    new CampaignsDTOResponse(),
  );
  const [isLoading, setIsLoading] = useState(true);

  const campaignsContainerRef = useRef<HTMLDivElement>(null);

  const sendCampaignMetric = (event: string, campaign: Campaign) => {
    agentService.tap(event, {
      organization_id: organizationId,
      shipment: {
        id: shipment.id,
        order_tracking: {
          order_uuid: shipment.orderTracking.orderUuid,
        },
      },
      campaign: {
        uuid: campaign.uuid,
      },
      tracking_experience: {
        slug: cmsSlug,
      },
    });
  };

  const onViewCampaign = (campaigns: Campaign[]) => {
    campaigns.forEach((campaign) => {
      sendCampaignMetric('campaign-view', campaign);
    });
  };

  const onClickCampaign = (campaign: Campaign) => {
    sendCampaignMetric('campaign-click', campaign);

    if (campaign.redirectUrl) {
      window.open(campaign.redirectUrl, '_blank');
    }
  };

  useEffect(() => {
    const getCampaigns = () => {
      if (campaignUuid) {
        return getCampaignsPreviewMode(organizationId, campaignUuid);
      }
      return getCampaignLiveMode(organizationId, shipment, cmsSlug);
    };
    getCampaigns()
      .then((campaigns) => {
        setData(campaigns);
      })
      .catch((error) => {
        if (isRuntimeError(error)) {
          ptpCatchError(error);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    let observer;
    if (data?.campaigns && data.campaigns.length > 0) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              onViewCampaign(data.campaigns);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        },
      );

      if (campaignsContainerRef.current) {
        observer.observe(campaignsContainerRef.current);
      }
    }
    return () => {
      if (campaignsContainerRef.current && observer) {
        observer.unobserve(campaignsContainerRef.current);
      }
    };
  }, [data.campaigns]);

  return {
    campaignsContainerRef,
    isLoading,
    data,
    onClickCampaign,
  };
};
